import { useEffect, useMemo, useState, useCallback } from "react";
import type { ReactNode } from "react";

// ─── Custom organization picker (double-/authorize flow) ─────────────────────
// Runs mid-login, between the two /authorize calls — outside AuthContext, not a
// ProtectedRoute. Authority is the Action-signed `session_token` in the URL,
// which also carries the login brand, so /org-selection/options returns only the
// orgs for the client the user signed in with. The list is DISPLAY ONLY — the
// second /authorize re-validates the pick, so DOM tampering can't grant access.
// Styling: black #111 / green / Calibri, Auth0-picker layout.

const BACKEND = process.env.REACT_APP_BACKEND_URL;

interface SelectableOrg {
  orgID: string;
  name: string;
  displayName: string;
}

// The mid-login session_token carries the login brand as an `application` code
// (AS | CG | IQ | TP | MC — set by the Auth0 Post-Login Action). Map it to the brand id
// the backend /logout understands so "Sign out" returns to the SAME portal the
// user signed in through. At the picker stage there is no id_token cookie yet,
// so /logout can't infer the brand on its own and would default to AskSmart.
const APPLICATION_TO_BRAND: Record<string, string> = {
  AS: "asksmart",
  CG: "caddiegames",
  IQ: "iqverity",
  TP: "tosp",
  MC: "maplecoin",
};

function brandFromSessionToken(token: string): string | null {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const b64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const claims = JSON.parse(atob(padded));
    const app = typeof claims.application === "string" ? claims.application : "";
    return APPLICATION_TO_BRAND[app] ?? null;
  } catch {
    return null;
  }
}

type Status = "loading" | "ready" | "continuing" | "error";

export default function SelectOrg() {
  const [status, setStatus] = useState<Status>("loading");
  const [orgs, setOrgs] = useState<SelectableOrg[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const { sessionToken, resumeState } = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      sessionToken: params.get("session_token") || "",
      resumeState: params.get("state") || "",
    };
  }, []);

  // Resume via /resume with the picked org on the query — the Action reads it in
  // onContinuePostLogin and stamps it as a claim. No client storage; re-validated
  // at the second /authorize.
  const chooseOrg = useCallback(
    (orgID: string) => {
      setStatus("continuing");
      // Same brand hint the Cancel button sends. If /resume rejects the state,
      // it can only show the right portal's "access denied" screen when it is
      // told which portal this is — mid-login there is no id_token cookie for
      // it to infer one from.
      const brand = brandFromSessionToken(sessionToken);
      const brandParam = brand ? `&brand=${encodeURIComponent(brand)}` : "";
      window.location.href =
        `${BACKEND}/resume?state=${encodeURIComponent(resumeState)}` +
        `&organization=${encodeURIComponent(orgID)}${brandParam}`;
    },
    [resumeState, sessionToken],
  );

  const cancel = useCallback(() => {
    // Mid-transaction: no cookie session yet, so /logout can't read the brand
    // from an id_token cookie — pass it explicitly from the session_token so we
    // return to the portal the user actually signed in through (not AskSmart).
    const brand = brandFromSessionToken(sessionToken);
    const query = brand ? `?brand=${encodeURIComponent(brand)}` : "";
    window.location.href = `${BACKEND}/logout${query}`;
  }, [sessionToken]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!sessionToken || !resumeState) {
        setErrorMessage(
          "This organization-selection link is missing or has expired. Please sign in again.",
        );
        setStatus("error");
        return;
      }

      // Ask the server for the orgs this login may select, authenticated by the
      // token. Brand filtering is server-side (keyed off the token's `application`
      // claim), so the page just renders whatever comes back.
      let organizations: SelectableOrg[];
      try {
        const res = await fetch(`${BACKEND}/org-selection/options`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ session_token: sessionToken }),
        });
        if (!res.ok) throw new Error(`options ${res.status}`);
        const data = await res.json();
        organizations = Array.isArray(data.organizations)
          ? data.organizations
          : [];
      } catch (err) {
        if (cancelled) return;
        console.warn("[SelectOrg] failed to load organizations:", err);
        setErrorMessage(
          "We couldn't load your organizations. Please sign in again.",
        );
        setStatus("error");
        return;
      }

      if (cancelled) return;

      const list = organizations.filter(
        (o) => o && typeof o.orgID === "string",
      );

      // Auto-skip a lone org (server-resolved; the second /authorize still gates it).
      if (list.length === 1) {
        chooseOrg(list[0].orgID);
        return;
      }

      setOrgs(list);
      setStatus(list.length === 0 ? "error" : "ready");
      if (list.length === 0) {
        setErrorMessage(
          "You don't have access to any organizations yet. Please contact your administrator.",
        );
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [sessionToken, resumeState, chooseOrg]);

  if (status === "loading" || status === "continuing") {
    return (
      <Shell>
        <div className="so-spinner" aria-hidden="true" />
        <p className="so-msg">
          {status === "continuing"
            ? "Getting things ready..."
            : "Loading your organizations..."}
        </p>
      </Shell>
    );
  }

  if (status === "error") {
    return (
      <Shell>
        <p className="so-msg">{errorMessage}</p>
        <button className="so-primary-btn" onClick={cancel}>
          Back to sign in
        </button>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="so-header">
        <h1 className="so-title">Choose an organization</h1>
        <p className="so-subtitle">
          Select the organization you'd like to work in.
        </p>
      </div>
      <ul className="so-list">
        {orgs.map((org) => {
          const label = org.displayName || org.name;
          const initial = (label || "?").trim().charAt(0) || "?";
          return (
            <li key={org.orgID}>
              <button
                className="so-org-btn"
                onClick={() => chooseOrg(org.orgID)}
                title={label}
              >
                <span className="so-avatar" aria-hidden="true">
                  {initial}
                </span>
                <span className="so-org-name">{label}</span>
                <span className="so-chevron" aria-hidden="true">
                  &rsaquo;
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      <button className="so-signout" onClick={cancel}>
        Sign out
      </button>
    </Shell>
  );
}

// ─── Dark themed page shell (Auth0-hosted-login look) ────────────────────────
function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="so-root" role="status" aria-live="polite">
      <style>{STYLES}</style>
      <div className="so-card">{children}</div>
    </div>
  );
}

const STYLES = `
  .so-root {
    min-height: 100vh;
    width: 100%;
    margin: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    padding: 24px;
    background: #111111;
    color: #ffffff;
    font-family: Calibri, sans-serif;
  }
  .so-card {
    width: 100%;
    max-width: 380px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    text-align: center;
  }
  .so-header { display: flex; flex-direction: column; gap: 8px; }
  .so-title {
    margin: 0;
    font-size: 27px;
    font-weight: 700;
    color: #ffffff;
  }
  .so-subtitle {
    margin: 0;
    font-size: 15px;
    color: rgba(255, 255, 255, 0.6);
  }
  .so-list {
    list-style: none;
    padding: 0;
    margin: 4px 0 0;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .so-org-btn {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    font-family: inherit;
    font-size: 16px;
    font-weight: 500;
    text-align: left;
    color: #ffffff;
    background: #1c1c1c;
    border: 1px solid #2a2a2a;
    border-radius: 8px;
    cursor: pointer;
    transition: border-color 0.15s ease, background 0.15s ease, transform 0.05s ease;
  }
  .so-org-btn:hover { background: #232323; border-color: #218838; }
  .so-org-btn:focus-visible {
    outline: none;
    border-color: #2ea043;
    box-shadow: 0 0 0 2px rgba(33, 136, 56, 0.5);
  }
  .so-org-btn:active { transform: translateY(1px); }
  .so-avatar {
    flex: 0 0 auto;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #218838;
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    font-weight: 700;
    text-transform: uppercase;
  }
  .so-org-name {
    flex: 1 1 auto;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .so-chevron {
    flex: 0 0 auto;
    color: rgba(255, 255, 255, 0.4);
    font-size: 24px;
    line-height: 1;
    transition: color 0.15s ease;
  }
  .so-org-btn:hover .so-chevron { color: #2ea043; }
  .so-signout {
    margin-top: 8px;
    background: none;
    border: none;
    color: #2ea043;
    font-family: inherit;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
  }
  .so-signout:hover { text-decoration: underline; }
  .so-primary-btn {
    padding: 12px 22px;
    font-family: inherit;
    font-size: 15px;
    font-weight: 600;
    color: #ffffff;
    background: #218838;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.15s ease;
  }
  .so-primary-btn:hover { background: #1e7e34; }
  .so-msg {
    max-width: 320px;
    margin: 0;
    font-size: 16px;
    line-height: 1.5;
    color: rgba(255, 255, 255, 0.85);
  }
  .so-spinner {
    width: 26px;
    height: 26px;
    border: 3px solid rgba(255, 255, 255, 0.15);
    border-top-color: #218838;
    border-radius: 50%;
    animation: so-spin 0.6s linear infinite;
  }
  @keyframes so-spin { to { transform: rotate(360deg); } }
`;
